// The controls at the head of the log that move the history cursor: to the
// start, one step back, one step forward, and back to the live game. They
// have no line of text. The cue bar says which step the board shows.

import { el } from '../lib/dom';
import { svg } from '../lib/svg';
import {
  goLive,
  goToStart,
  type HistoryState,
  historyStore,
  stepBack,
  stepForward,
} from '../state/history';

const ICON_BOX = 24;

/** Each control is a solid shape: a triangle for a step, and a bar where the step stops at an end. */
const ICONS = {
  start: 'M4 4 H7 V20 H4 Z M20 4 V20 L9 12 Z',
  back: 'M18 4 V20 L6 12 Z',
  forward: 'M6 4 V20 L18 12 Z',
  live: 'M4 4 V20 L15 12 Z M17 4 H20 V20 H17 Z',
} as const;

const navButton = (
  icon: keyof typeof ICONS,
  label: string,
  tip: string,
  onclick: () => void,
): HTMLButtonElement =>
  el(
    'button',
    {
      className: 'button history__button',
      type: 'button',
      ariaLabel: label,
      dataset: { tip },
      onclick,
    },
    [
      svg(
        'svg',
        { class: 'history__icon', viewBox: `0 0 ${ICON_BOX} ${ICON_BOX}`, 'aria-hidden': 'true' },
        [svg('path', { d: ICONS[icon] })],
      ),
    ],
  );

export const createHistoryNav = (): HTMLElement => {
  const start = navButton('start', 'Start', 'Show the start of the match. Home key.', goToStart);
  const back = navButton('back', 'Back', 'Show one step back. Left arrow key.', stepBack);
  const forward = navButton(
    'forward',
    'Forward',
    'Show one step forward. Right arrow key.',
    stepForward,
  );
  const live = navButton('live', 'Live', 'Return to the live game. End key.', goLive);
  const root = el('nav', { className: 'history', ariaLabel: 'Match history' }, [
    start,
    back,
    forward,
    live,
  ]);

  const draw = ({ steps, cursor, liveIsAhead }: HistoryState): void => {
    const lastStep = steps.length - 1;
    const reviewing = cursor !== null;
    const canGoBack = reviewing ? cursor > 0 : lastStep > 0 || liveIsAhead;
    start.disabled = !canGoBack;
    back.disabled = !canGoBack;
    forward.disabled = !reviewing;
    live.disabled = !reviewing;
    live.classList.toggle('button--primary', reviewing);
    root.classList.toggle('history--reviewing', reviewing);
  };

  draw(historyStore.get());
  historyStore.subscribe(draw);
  return root;
};
